import React from 'react';
import './Pagination.css';

const PAGE_SIZE_OPTIONS = [10, 15, 25, 50];

/**
 * Reusable pagination component with configurable page size.
 *
 * Props:
 *  - currentPage: number
 *  - totalPages: number
 *  - pageSize: number
 *  - totalItems: number
 *  - displayedItems: number
 *  - onPageChange: (page) => void
 *  - onPageSizeChange: (size) => void
 *  - pageSizeOptions: number[] (optional, defaults to [10, 15, 25, 50])
 */
function Pagination({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  displayedItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
}) {
  const buildPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (totalPages <= 7) {
        pages.push(i);
      } else if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 1) {
        pages.push(i);
      }
    }

    // Insert ellipsis markers between gaps
    const withEllipsis = [];
    for (let j = 0; j < pages.length; j++) {
      if (j > 0 && pages[j] - pages[j - 1] > 1) {
        withEllipsis.push({ type: 'ellipsis', key: `ellipsis-${pages[j]}` });
      }
      withEllipsis.push({ type: 'page', value: pages[j] });
    }
    return withEllipsis;
  };

  return (
    <div className="pagination-container">
      {/* Page size selector */}
      <div className="pagination-controls">
        <label className="pagination-size-label">
          Rows per page:
          <select
            className="pagination-size-select"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Page buttons */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Prev
          </button>

          {buildPageNumbers().map((item) =>
            item.type === 'ellipsis' ? (
              <span key={item.key} className="pagination-ellipsis">
                ...
              </span>
            ) : (
              <button
                key={item.value}
                className={`pagination-btn ${currentPage === item.value ? 'active' : ''}`}
                onClick={() => onPageChange(item.value)}
              >
                {item.value}
              </button>
            )
          )}

          <button
            className="pagination-btn"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Result count */}
      <p className="result-count">
        Showing {displayedItems} of {totalItems} result
        {totalItems !== 1 ? 's' : ''}
      </p>
    </div>
  );
}

export default Pagination;
